import type { ChangeEvent, InputHTMLAttributes } from 'react';
import { mascaraCPF, mascaraTelefone } from '@/pages/inscricao/utils';

type Mascara = 'cpf' | 'telefone' | 'dinheiro';

function mascaraDinheiro(v: string): string {
  const d = v.replace(/\D/g, '');
  if (!d) return '';
  const n = parseInt(d, 10) / 100;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function aplicarMascara(mascara: Mascara | undefined, v: string): string {
  if (mascara === 'cpf') return mascaraCPF(v);
  if (mascara === 'telefone') return mascaraTelefone(v);
  if (mascara === 'dinheiro') return mascaraDinheiro(v);
  return v;
}

const INPUT_MODE: Partial<Record<Mascara, 'numeric' | 'tel'>> = {
  cpf: 'numeric',
  telefone: 'tel',
  dinheiro: 'numeric',
};

interface CampoProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  rotulo?: string;
  ajuda?: string;
  erro?: string | null;
  mascara?: Mascara;
  onChange?: (valor: string) => void;
}

export default function Campo({
  rotulo,
  ajuda,
  erro,
  mascara,
  onChange,
  className = '',
  type = 'text',
  ...rest
}: CampoProps) {
  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const valor = mascara ? aplicarMascara(mascara, ev.target.value) : ev.target.value;
    onChange?.(valor);
  };

  return (
    <div className={`w-full ${className}`}>
      {rotulo && (
        <label className="mb-1.5 block text-sm font-medium text-foreground-800">{rotulo}</label>
      )}
      <input
        type={type}
        inputMode={mascara ? INPUT_MODE[mascara] : undefined}
        onChange={handleChange}
        className={`h-12 w-full rounded-xl border bg-white px-3.5 text-base text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 ${
          erro ? 'border-danger-500' : 'border-secondary-300 focus:border-primary-400'
        }`}
        {...rest}
      />
      {erro ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-danger-600" role="alert">
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {erro}
        </p>
      ) : ajuda ? (
        <p className="mt-1.5 text-sm text-foreground-500">{ajuda}</p>
      ) : null}
    </div>
  );
}